import type { MarketConfig } from '../core/internal/market-config-catalog.js';
import {
  CONVICTION_CHART_LIVE_DETAIL_WINDOW_MS,
  type ConvictionChartSample,
  createSparseConvictionChartSampler,
  type ParsedConvictionChartState,
  type SparseConvictionChart,
} from './conviction-chart.js';
import { convictionFraction } from './conviction-percent.js';
import {
  type MarketResolutionThresholdPolicy,
  resolutionThresholdStateAt,
} from './resolution-threshold.js';
import {
  type ProjectMarketResolutionAtResult,
  projectMarketResolutionAt,
} from './resolution-threshold-projection.js';

/**
 * HARD RULE: this is a presentation clock, not an ingestion sleep or a claim
 * about Solana commitment. Events retain contract event time even when chain,
 * indexer, or NATS delivery is late. The delayed head normally keeps that late
 * update in the unseen tail; delivery beyond this window corrects the original
 * tail and the renderer applies its one-second morph. Never shift an event to
 * receipt time or apply this delay to trading, Price, or Market Cap.
 */
export const AUTHORITATIVE_CONVICTION_CHART_DELAY_MS = 1_500;

export interface AuthoritativeConvictionChartClock {
  estimate(): {
    readonly serverNowMs: number;
    readonly usable: boolean;
  };
}

export interface AuthoritativeConvictionChartPresentationSource {
  readonly chart: SparseConvictionChart;
  readonly geometryRevision: string;
  readonly earliestAffectedAtMs: number | null;
}

export interface AuthoritativeConvictionChartPresentationThreshold {
  readonly config: MarketConfig | undefined;
  readonly policy: MarketResolutionThresholdPolicy;
  readonly status: string;
  readonly tradingStartedAt: string | null;
  readonly resolvedAt: string | null;
}

export interface AuthoritativeConvictionChartPresentationUpdate {
  readonly source: AuthoritativeConvictionChartPresentationSource | null;
  readonly clock: AuthoritativeConvictionChartClock | null;
  readonly degraded: boolean;
  readonly threshold?: AuthoritativeConvictionChartPresentationThreshold;
}

export interface AuthoritativeConvictionChartPoint {
  readonly time: number;
  readonly value: number;
}

export interface AuthoritativeConvictionChartOutcome {
  readonly outcomeIndex: number;
  readonly data: ReadonlyArray<AuthoritativeConvictionChartPoint>;
  readonly value: number;
}

export interface AuthoritativeConvictionChartFrame {
  readonly geometryRevision: string;
  readonly earliestAffectedAtMs: number | null;
  readonly logicalHeadSeconds: number;
  readonly headSeconds: number;
  readonly outcomes: ReadonlyArray<AuthoritativeConvictionChartOutcome>;
  readonly degraded: boolean;
  readonly thresholdFraction?: number;
  readonly forecast: ProjectMarketResolutionAtResult | null;
}

export interface AuthoritativeConvictionChartPresentationSampler {
  update(input: AuthoritativeConvictionChartPresentationUpdate): void;
  sample(monotonicNowMs: number): AuthoritativeConvictionChartFrame | null;
}

export function createAuthoritativeConvictionChartPresentationSampler(): AuthoritativeConvictionChartPresentationSampler {
  let source: AuthoritativeConvictionChartPresentationSource | null = null;
  let clock: AuthoritativeConvictionChartClock | null = null;
  let chartSampler: ReturnType<typeof createSparseConvictionChartSampler> | null = null;
  let samplerRevision: string | null = null;
  let historyCache: {
    readonly revision: string;
    readonly samples: ReadonlyArray<ConvictionChartSample>;
  } | null = null;
  let threshold: AuthoritativeConvictionChartPresentationThreshold | undefined;
  let degraded = false;

  return {
    update(input): void {
      source = input.source;
      clock = input.clock;
      degraded = input.degraded;
      threshold = input.threshold;
      if (source === null) {
        chartSampler = null;
        samplerRevision = null;
        historyCache = null;
      } else if (source.geometryRevision !== samplerRevision) {
        chartSampler = createSparseConvictionChartSampler(source.chart);
        samplerRevision = source.geometryRevision;
        historyCache = null;
      }
    },
    sample(_monotonicNowMs): AuthoritativeConvictionChartFrame | null {
      if (source === null || clock === null || chartSampler === null) return null;
      const estimate = clock.estimate();
      if (!estimate.usable) return null;
      const logicalHeadMs = Math.floor(
        estimate.serverNowMs - AUTHORITATIVE_CONVICTION_CHART_DELAY_MS,
      );
      if (logicalHeadMs < source.chart.tradingStartedAtMs) return null;
      const headMs =
        source.chart.terminalAtMs === null
          ? logicalHeadMs
          : Math.min(logicalHeadMs, source.chart.terminalAtMs);
      if (historyCache?.revision !== source.geometryRevision) {
        historyCache = {
          revision: source.geometryRevision,
          samples: chartSampler.materialize(),
        };
      }
      const samples = presentationSamples(source.chart, historyCache.samples, chartSampler, headMs);
      const head = chartSampler.sample(headMs);
      const outcomes = Array.from({ length: source.chart.outcomeCount }, (_, outcomeIndex) => ({
        outcomeIndex,
        data: samples.map((sample) => ({
          time: sample.timeMs / 1_000,
          value: convictionFraction(sample.valuesPpb[outcomeIndex]),
        })),
        value: convictionFraction(head.valuesPpb[outcomeIndex]),
      })).sort((left, right) => right.value - left.value || left.outcomeIndex - right.outcomeIndex);
      return {
        geometryRevision: source.geometryRevision,
        earliestAffectedAtMs: source.earliestAffectedAtMs,
        logicalHeadSeconds: logicalHeadMs / 1_000,
        headSeconds: headMs / 1_000,
        outcomes,
        degraded,
        forecast: resolveForecast(source.chart, threshold, headMs),
        ...resolveThreshold(threshold, headMs),
      };
    },
  };
}

function presentationSamples(
  chart: SparseConvictionChart,
  materialized: ReadonlyArray<ConvictionChartSample>,
  sampler: ReturnType<typeof createSparseConvictionChartSampler>,
  headMs: number,
): ReadonlyArray<ConvictionChartSample> {
  if (chart.bucketSeconds === 1) {
    const liveStartMs = Math.max(
      chart.tradingStartedAtMs,
      headMs - CONVICTION_CHART_LIVE_DETAIL_WINDOW_MS,
    );
    const historical = materialized.filter((sample) => sample.timeMs < liveStartMs);
    return [...historical, ...sampleWholeSeconds(sampler.sample, liveStartMs, headMs)];
  }

  const openingMs = Math.max(chart.fromMs, chart.tradingStartedAtMs);
  const bucketWidthMs = chart.bucketSeconds * 1_000;
  const historical = materialized.filter(
    (sample) =>
      sample.timeMs < headMs &&
      (sample.timeMs === openingMs || sample.timeMs % bucketWidthMs === 0),
  );
  return [...historical, sampler.sample(headMs)];
}

function sampleWholeSeconds(
  sample: (sampleMs: number) => ConvictionChartSample,
  fromMs: number,
  throughMs: number,
): ReadonlyArray<ConvictionChartSample> {
  const result: ConvictionChartSample[] = [];
  for (let timeMs = Math.ceil(fromMs / 1_000) * 1_000; timeMs < throughMs; timeMs += 1_000) {
    result.push(sample(timeMs));
  }
  result.push(sample(throughMs));
  return result;
}

function resolveForecast(
  chart: SparseConvictionChart,
  input: AuthoritativeConvictionChartPresentationThreshold | undefined,
  headMs: number,
): ProjectMarketResolutionAtResult | null {
  if (input === undefined || input.status !== 'trading') return null;
  const state = governingState(chart.states, headMs);
  if (state === null) return null;
  return projectMarketResolutionAt({
    pipelineStage: 'open',
    config: input.config,
    outcomeCount: chart.outcomeCount,
    tradingStartedAt: input.tradingStartedAt,
    convictionUpdatedAt: String(Math.floor(state.sourceEventTimeMs / 1_000)),
    retentionPerSecondPpb: chart.retentionPerSecondPpb.toString(),
    outcomes: state.anchorsPpb.map((anchor, outcomeIndex) => ({
      outcomeIndex,
      convictionAnchor: anchor.toString(),
      convictionTarget: (state.targetsPpb[outcomeIndex] as bigint).toString(),
    })),
  });
}

function governingState(
  states: ReadonlyArray<ParsedConvictionChartState>,
  headMs: number,
): ParsedConvictionChartState | null {
  let active: ParsedConvictionChartState | null = null;
  for (const state of states) {
    if (state.sourceEventTimeMs > headMs) break;
    active = state;
  }
  return active;
}

function resolveThreshold(
  input: AuthoritativeConvictionChartPresentationThreshold | undefined,
  headMs: number,
): { readonly thresholdFraction?: number } {
  if (input === undefined) return {};
  const resolvedAtMs = input.resolvedAt === null ? Number.NaN : Date.parse(input.resolvedAt);
  const status =
    (input.status === 'resolved' || input.status === 'graduated') &&
    Number.isFinite(resolvedAtMs) &&
    headMs < resolvedAtMs
      ? 'trading'
      : input.status;
  const state = resolutionThresholdStateAt({
    ...input,
    status,
    nowTs: BigInt(Math.floor(headMs / 1_000)),
  });
  return state.kind === 'ready' ? { thresholdFraction: state.thresholdFraction } : {};
}
